import './index.css'
import upperFirst from 'lodash/upperFirst'
import * as React from 'react'
import { connect } from 'react-redux'
import { Outlet } from 'react-router-dom'
import type { State } from 'types/redux'
import type { BusinessInvite, BusinessUser, User } from 'types/user'
import {
  cancelBusinessInvite,
  changeBusinessUser,
  loadBusinessInvites,
  loadBusinessUsers,
  removeBusinessUser,
  resendBusinessInvite,
} from '@disco/state-management/actions/BusinessUsersActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getIsTrial } from '@disco/state-management/selectors/business'
import {
  getInviteModalLink,
  isDisableInviteTeamMember,
} from '@disco/state-management/selectors/invites'
import type { sortedBusinessUsers } from '@disco/state-management/selectors/user'
import { getBusinessUsers } from '@disco/state-management/selectors/user'
import { getEntityName } from '@disco/utils/entityName'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import UserName from 'components/common/user-name'
import UsersQuotaAlert from 'components/common/users-quota-alert'
import DocumentHeadTags from 'components/document-head-tags'
import { Spinner } from 'components/ui'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Icon from 'components/ui/icon'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Stack from 'components/ui/stack'
import TextField from 'components/ui/text-field'
import LegacyPlansAlert from './legacy-plans-alert'

function mapStateToProps(state) {
  return {
    user: state.user,
    businessInvites: state.entities.businessInvites,
    businessUsers: getBusinessUsers(state),
    isSubscriber: !getIsTrial(state),
    disableInviteTeamMember: isDisableInviteTeamMember(state),
    inviteModalLink: getInviteModalLink(state),
    isPhone: isDeviceTypePhone(state),
  }
}

const mapDispatchToProps = {
  loadBusinessUsers,
  loadBusinessInvites,
  changeBusinessUser,
  removeBusinessUser,
  resendBusinessInvite,
  cancelBusinessInvite,
}

const getEntityNameOrID = entity =>
  getEntityName(entity) || `${upperFirst(entity.type)} #${entity.id}`

type Props = {
  user: User
  loading: boolean
  error: Error | null | undefined
  businessInvites: State['entities']['businessInvites']
  businessUsers: sortedBusinessUsers
  isSubscriber: boolean
  isPhone: boolean
  disableInviteTeamMember: boolean
  inviteModalLink: string
} & typeof mapDispatchToProps

class UsersSettings extends React.Component<
  Props,
  {
    confirmationDialog:
      | {
          caption: string
          message: string
          confirmButtonCaption: string
          onConfirm: (...args: Array<any>) => any
        }
      | null
      | undefined
    error: Error | null | undefined
    loading: boolean
    search?: string
  }
> {
  state = {
    confirmationDialog: null,
    error: null,
    loading: true,
    search: undefined,
  }

  async componentDidMount() {
    try {
      if (
        !this.props.businessUsers.loading &&
        !this.props.businessUsers.loaded
      ) {
        await this.props.loadBusinessUsers()
      }

      if (
        !this.props.businessInvites.loading &&
        !this.props.businessInvites.loaded
      ) {
        await this.props.loadBusinessInvites()
      }

      this.setState({
        error: null,
        loading: false,
      })
    } catch (error) {
      this.setState({
        error,
        loading: false,
      })
    }
  }

  _onActionClick = (entity, action) => {
    const name = getEntityNameOrID(entity.user ? entity.user : entity)

    switch (action) {
      case 'Delete user':
        this.setState({
          confirmationDialog: {
            caption: 'Delete user',
            message: `Remove ${name} from the team?`,
            confirmButtonCaption: 'Yes, remove',
            onConfirm: () => this.props.removeBusinessUser(entity.id),
          },
        })
        break

      case 'Make Admin':
        this.setState({
          confirmationDialog: {
            caption: 'Make Admin',
            message: `Change ${name} to Admin?`,
            confirmButtonCaption: 'Yes',
            onConfirm: () =>
              this.props.changeBusinessUser(entity.id, {
                type: 'administrator',
              }),
          },
        })
        break

      case 'Make User':
        this.setState({
          confirmationDialog: {
            caption: 'Make User',
            message: `Change ${name} to User?`,
            confirmButtonCaption: 'Yes',
            onConfirm: () =>
              this.props.changeBusinessUser(entity.id, {
                type: 'user',
              }),
          },
        })
        break

      case 'Resend':
        this.setState({
          confirmationDialog: {
            caption: 'Resend invite',
            message: `Resend invitation to ${name}?`,
            confirmButtonCaption: 'Yes, resend',
            onConfirm: () => this.props.resendBusinessInvite(entity.id),
          },
        })
        break

      case 'Cancel invite':
        this.setState({
          confirmationDialog: {
            caption: 'Cancel invite',
            message: `Cancel invitation to ${name}?`,
            confirmButtonCaption: 'Yes',
            onConfirm: () => this.props.cancelBusinessInvite(entity.id),
          },
        })
        break

      default:
        throw new Error('Not implemented')
    }
  }
  _getTableData = () => {
    const invites: BusinessInvite[] = Object.values(
      this.props.businessInvites.invites,
    )
    const businessUsers: BusinessUser[] = this.props.businessUsers.users
    let data = [...businessUsers, ...invites]
    const search = (this.state.search || '').toLowerCase()
    if (this.state.search) {
      data = data.filter((item: BusinessInvite | BusinessUser) => {
        const userSettings = 'user' in item && item.user ? item.user : item
        const email = 'email' in userSettings ? userSettings.email || '' : ''
        const firstName =
          'first_name' in userSettings ? userSettings.first_name || '' : ''
        const lastName =
          'last_name' in userSettings ? userSettings.last_name || '' : ''
        const string = [email, firstName, lastName].join(' ')
        return string.toLowerCase().includes(search)
      })
    }

    return data
  }

  render() {
    // todo: refactor <MyTeam> + <PricingTable>
    const { confirmationDialog } = this.state
    const { isPhone } = this.props

    if (this.state.loading) {
      return <Spinner />
    }

    if (this.state.error) {
      return (
        <div className="Admin_Form">
          <DocumentHeadTags title="Users | DISCO" />
          <div className="Admin_Section">
            <h2 className="Admin_Header">Users</h2>
            <div className="warning-block">{this.state.error.toString()}</div>
          </div>
        </div>
      )
    }

    let invitesDisabled
    const canManageInvitations = this.props.user?.permissions?.includes(
      'can_manage_invitations',
    )

    if (!canManageInvitations) {
      invitesDisabled = true
    }

    // TODO: extract `=== 'saml'` checks
    if (this.props.user.business.login_mechanism === 'saml') {
      invitesDisabled = true
    }

    const isCurrentUser = (bu: BusinessUser) =>
      bu.user.id === this.props.user.id

    return (
      <>
        <DocumentHeadTags title="Users | DISCO" />
        <PageHeader title="Users" />
        <Stack space="small">
          <UsersQuotaAlert />

          <LegacyPlansAlert />

          <PageContainer>
            {!isPhone && (
              <Row columnsSize={[3]} marginBottom={false}>
                <div>
                  <TextField
                    nodeRight={<Icon type="search" />}
                    placeholder="Search"
                    value={this.state.search}
                    onChange={event => {
                      this.setState({
                        search: event?.target?.value,
                      })
                    }}
                  />
                </div>
              </Row>
            )}

            <DataTable
              appearance="plain"
              columns={[
                {
                  name: 'USER NAME',
                  width: 'content',
                  render: (props: Record<string, any>) => (
                    <UserName user={props.user ? props.user : props} />
                  ),
                },
                {
                  name: 'EMAIL',
                  render: (props: Record<string, any>) =>
                    props.user ? props.user.email : props.email,
                },
                {
                  name: 'LABEL',
                  width: 'content',
                  render: (props: Record<string, any>) => {
                    const roles = []

                    if (props.user) {
                      if (isCurrentUser(props as any)) {
                        roles.push('You!')
                      }

                      if (props.removed) {
                        roles.push('Removed')
                      }

                      switch (props.type) {
                        case 'user':
                          roles.push('User')
                          break

                        case 'administrator':
                          roles.push('Admin')
                          break
                      }
                    } else {
                      if (props.cancelled) {
                        roles.push('Cancelled')
                      } else {
                        if (props.cancelled) {
                          roles.push('Cancelled')
                        } else {
                          roles.push('Invited')
                        }
                      }
                    }

                    return roles.map((role, index) => (
                      <div
                        key={index}
                        className={`MyTeam_Badge MyTeam_Badge--${role}`}
                      >
                        {role}
                      </div>
                    ))
                  },
                },
                {
                  name: 'ACTIONS',
                  contentType: 'button',
                  width: 'content',
                  render: (props: Record<string, any>) => (
                    <EllipsisMenu
                      compact
                      options={
                        props.user
                          ? [
                              {
                                label:
                                  props.type === 'administrator'
                                    ? 'Make User'
                                    : 'Make Admin',
                                value:
                                  props.type === 'administrator'
                                    ? 'Make User'
                                    : 'Make Admin',
                                disabled: !props.manageable || props.removed,
                              },
                              {
                                label: 'Delete user',
                                value: 'Delete user',
                                destructive: true,
                                disabled: !props.manageable || props.removed,
                              },
                            ]
                          : [
                              {
                                label: 'Resend',
                                value: 'Resend',
                                disabled: invitesDisabled || props.cancelled,
                              },
                              {
                                label: 'Cancel invite',
                                value: 'Cancel invite',
                                disabled: invitesDisabled || props.cancelled,
                              },
                            ]
                      }
                      icon="ellipsis"
                      hasArrow={false}
                      onOptionClick={value => this._onActionClick(props, value)}
                    />
                  ),
                },
              ]}
              data={this._getTableData()}
              emptyState="No team members here yet! Invite them via the button above."
            />
            {confirmationDialog && (
              <ModalDialog
                title={confirmationDialog.caption}
                primaryAction={{
                  caption: confirmationDialog.confirmButtonCaption,
                  onClick: () => {
                    this.setState({
                      confirmationDialog: null,
                    })
                    confirmationDialog.onConfirm()
                  },
                }}
                status="danger"
                onCancel={() =>
                  this.setState({
                    confirmationDialog: null,
                  })
                }
              >
                {confirmationDialog.message}
              </ModalDialog>
            )}
          </PageContainer>
        </Stack>

        <Outlet />
      </>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UsersSettings) as any
